/**
 * Task edit form. Pre-fills from an existing task and edits its title and
 * type-specific data (due date, urgent flag, lab type).
 *
 * Task type, patient, and file attachments are fixed once a task is created,
 * so they are not editable here. Reassignment is handled separately by the
 * task action menu.
 */

import { type Duration, format, parseISO, startOfToday } from 'date-fns'
import { useId } from 'react'
import { z } from 'zod'

import { LAB_TYPES, labTypeSchema, type PatchTaskBody, type TaskResponse } from '@snootsvet/shared'

import { useAppForm } from '@/frontend/components/form'
import { Button } from '@/frontend/components/ui/button'
import { Checkbox } from '@/frontend/components/ui/checkbox'

import { useUpdateTask } from '../use-update-task'

const DUE_DATE_PRESETS: Duration[] = [{ days: 1 }, { days: 3 }, { days: 7 }]

/** Task types that can be edited through this form: everything but the system-generated ones. */
type EditableTaskType = Exclude<TaskResponse['type'], 'wet_script_request' | 'procedure_request'>

/** Builds a validation schema tailored to the task's type. */
function buildEditSchema(type: EditableTaskType) {
  return z.object({
    title: z.string().min(1, 'Title is required'),
    dueDate:
      type === 'follow_up' ? z.date({ message: 'Due date is required' }) : z.date().nullable(),
    urgent: z.boolean(),
    // Lab type is optional — `''` represents "not specified".
    labType: z.union([labTypeSchema, z.literal('')]),
  })
}

type EditFormValues = {
  title: string
  dueDate: Date | null
  urgent: boolean
  labType: z.infer<typeof labTypeSchema> | ''
}

/** Extracts the pre-filled form values from an existing task. */
function toDefaultValues(task: TaskResponse): EditFormValues {
  const data = task.data
  return {
    title: task.title,
    dueDate: 'dueDate' in data && data.dueDate ? parseISO(data.dueDate) : null,
    urgent: 'urgent' in data ? data.urgent : false,
    labType: data.type === 'labs' ? (data.labType ?? '') : '',
  }
}

/** Assembles the `data` payload for the PATCH request based on the task type. */
function toDataPayload(type: EditableTaskType, values: EditFormValues): PatchTaskBody['data'] {
  // PATCH replaces the whole `data` object, so we must carry the existing due
  // date back into the payload — otherwise editing another field would erase it.
  const dueDate = values.dueDate ? format(values.dueDate, 'yyyy-MM-dd') : undefined
  switch (type) {
    case 'follow_up':
      return { type: 'follow_up', dueDate: format(values.dueDate!, 'yyyy-MM-dd') }
    case 'labs':
      return { type: 'labs', urgent: values.urgent, labType: values.labType || undefined, dueDate }
    case 'medication_request':
      return { type: 'medication_request', urgent: values.urgent, dueDate }
    case 'prescription_request':
      return { type: 'prescription_request', urgent: values.urgent, dueDate }
    case 'referral':
      return { type: 'referral', urgent: values.urgent, dueDate }
    case 'other':
      return { type: 'other', urgent: values.urgent, dueDate }
  }
}

export function EditTaskForm(props: {
  task: TaskResponse
  onSuccess?: () => void
  onCancel?: () => void
}) {
  // Wet scripts and procedure requests carry system snapshots in `data` and aren't editable
  // through this form. The action menu already guards against opening it, but keep the invariant local.
  if (props.task.type === 'wet_script_request' || props.task.type === 'procedure_request') {
    throw new Error(`EditTaskForm does not support ${props.task.type} tasks`)
  }
  const type = props.task.type
  const { mutate, isPending } = useUpdateTask(props.task.id)
  const urgentId = useId()

  const form = useAppForm({
    defaultValues: toDefaultValues(props.task),
    validators: {
      onSubmit: buildEditSchema(type),
    },
    onSubmit: ({ value, formApi }) => {
      mutate(
        { title: value.title, data: toDataPayload(type, value) },
        {
          onSuccess: () => props.onSuccess?.(),
          onError: (err) => {
            formApi.setErrorMap({
              onSubmit: {
                form: err instanceof Error ? err.message : 'Failed to update task',
                fields: {},
              },
            })
          },
        }
      )
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <div className="flex flex-col gap-4 py-2">
          <form.GlobalError />

          {type === 'labs' && (
            <form.AppField name="labType">
              {(field) => (
                <field.SelectField
                  label="Lab type"
                  items={[{ label: 'Not specified', value: '' }, ...LAB_TYPES]}
                />
              )}
            </form.AppField>
          )}

          <form.AppField name="title">
            {(field) => <field.InputField label="Title" placeholder="Brief summary" required />}
          </form.AppField>

          {type === 'follow_up' && (
            <form.AppField name="dueDate">
              {(field) => (
                <field.DatePickerField
                  label="Due date"
                  fromDate={startOfToday()}
                  presets={DUE_DATE_PRESETS}
                  required
                />
              )}
            </form.AppField>
          )}

          {type !== 'follow_up' && (
            <form.Field name="urgent">
              {(field) => (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={urgentId}
                    checked={field.state.value}
                    onCheckedChange={(checked) => field.handleChange(checked === true)}
                  />
                  <label htmlFor={urgentId} className="font-medium cursor-pointer">
                    Urgent
                  </label>
                </div>
              )}
            </form.Field>
          )}
        </div>

        <form.Subscribe selector={(s) => [s.canSubmit, s.isSubmitting] as const}>
          {([canSubmit, isSubmitting]) => (
            <div className="flex items-center justify-end gap-2 pt-3">
              {props.onCancel && (
                <Button variant="outline" size="lg" type="button" onClick={props.onCancel}>
                  Cancel
                </Button>
              )}
              <Button type="submit" size="lg" disabled={!canSubmit || isSubmitting || isPending}>
                {isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </form.Subscribe>
      </form.AppForm>
    </form>
  )
}
